// Tutorial slide 1/5. Static illustration of the Discover screen's swipe deck
// with a peek of the card behind. Teaches "swipe right to match" and hints at
// the Browse alternative. No interactivity — swiping a fake card isn't the
// magic feature, so we just narrate it.

import { ChevronRight, Heart, X, LayoutGrid } from 'lucide-react';
import { Portrait } from '@/components/portraits';
import { C } from '@/styles/tokens';

interface MatchDemoProps {
  onContinue: () => void;
}

export function MatchDemo({ onContinue }: MatchDemoProps) {
  return (
    <div className="flex-1 flex flex-col">
      <div className="ff-display text-3xl mb-2" style={{ color: C.ink }}>
        Find a friend.
      </div>
      <div className="ff-body text-[14px] leading-relaxed mb-6" style={{ color: C.inkLight }}>
        Twenty AI personas across China. Swipe right to match, left to skip — or tap Browse to see everyone at once.
      </div>

      <div className="flex-1 flex items-center justify-center relative">
        <div
          aria-hidden
          className="absolute w-[220px] h-[280px] rounded-3xl"
          style={{
            background: 'linear-gradient(135deg, #CFE9E5, #5BA89F)',
            transform: 'translate(14px, 10px) rotate(4deg)',
            opacity: 0.5,
          }}
        />
        <div
          className="relative w-[240px] h-[300px] rounded-3xl overflow-hidden anim-scale-in"
          style={{
            boxShadow: '0 18px 40px -16px rgba(26,22,20,0.35)',
            transform: 'rotate(-3deg)',
          }}
        >
          <Portrait id="xiaomei" />
          {/* Bottom gradient scrim so the white text remains legible over the photo. */}
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-32"
            style={{ background: 'linear-gradient(to top, rgba(26,22,20,0.7), rgba(26,22,20,0))' }}
          />
          <div className="absolute inset-x-0 bottom-0 flex flex-col justify-end p-4 text-white">
            <div className="ff-zh-serif text-2xl font-semibold leading-none mb-0.5">小美</div>
            <div className="ff-body text-[11px] italic opacity-90 mb-1.5">Xiǎo Měi · 24 · Beijing</div>
            <div className="ff-body text-[12px] leading-snug opacity-95">Barista at a tiny hutong cafe</div>
          </div>
          <div
            className="absolute top-3 left-3 px-2 py-1 rounded-full ff-body text-[10px] uppercase tracking-[0.15em] font-bold"
            style={{ background: 'rgba(255,255,255,0.9)', color: C.vermillion }}
          >
            HSK 1
          </div>
          <span
            aria-hidden
            className="absolute top-1/3 right-3 ff-body text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-full anim-pulse-glow"
            style={{
              background: C.jade,
              color: 'white',
              transform: 'rotate(8deg)',
            }}
          >
            Match
          </span>
        </div>

        <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-6" aria-hidden>
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center"
            style={{ background: 'white', border: `1px solid ${C.mist}`, boxShadow: '0 2px 6px rgba(26,22,20,0.08)' }}
          >
            <X size={18} style={{ color: C.inkSofter }} />
          </div>
          <div className="ff-body text-[11px] inline-flex items-center gap-1" style={{ color: C.inkSofter }}>
            <LayoutGrid size={12} />
            or Browse
          </div>
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center"
            style={{ background: C.vermillion, boxShadow: '0 4px 10px rgba(233,75,60,0.35)' }}
          >
            <Heart size={18} fill="white" style={{ color: 'white' }} />
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onContinue}
        className="mt-6 py-3 rounded-full ff-body font-semibold text-white inline-flex items-center justify-center gap-2"
        style={{ background: C.vermillion }}
      >
        Then what?
        <ChevronRight size={16} aria-hidden />
      </button>
    </div>
  );
}
